import React, { useState, useEffect } from 'react';
import { LauncherModule } from './client/modules/launcher/LauncherModule';
import { PortalHeader, PortalRole } from './components/layout/PortalHeader';
import { ForemanModule } from './client/modules/foreman/ForemanModule';
import { PtoModule } from './client/modules/pto/PtoModule';
import { HRModule } from './client/modules/hr/HRModule';
import { AdminModule } from './client/modules/admin/AdminModule';
import { Lock, AlertCircle, X } from 'lucide-react';

export const App: React.FC = () => {
  const [currentPath, setCurrentPath] = useState<string>(window.location.pathname || '/');
  const [authRole, setAuthRole] = useState<string | null>(
    localStorage.getItem('construction_monitor_role')
  );
  const [serverStatus, setServerStatus] = useState<any>(null);
  const [alertsCount, setAlertsCount] = useState<number>(0);

  // Route Guard modal PIN state when visiting direct route without auth
  const [guardPinInput, setGuardPinInput] = useState('');
  const [guardError, setGuardError] = useState<string | null>(null);

  // Sync route on popstate (browser back/forward)
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname || '/');
      setAuthRole(localStorage.getItem('construction_monitor_role'));
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Fetch background status & alerts
  const fetchHealth = async () => {
    try {
      const res = await fetch('/api/v1/health');
      if (res.ok) {
        const data = await res.json();
        setServerStatus(data);
      }
    } catch (err) {
      console.error('Health check failed:', err);
    }
  };

  const fetchAlerts = async () => {
    try {
      const res = await fetch('/api/v1/hr/alerts');
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setAlertsCount(data.data.totalAlerts || 0);
        }
      }
    } catch (err) {
      console.error('Alerts fetch failed:', err);
    }
  };

  useEffect(() => {
    fetchHealth();
    fetchAlerts();
    const interval = setInterval(() => {
      fetchHealth();
      fetchAlerts();
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  const navigate = (path: string) => {
    window.history.pushState({}, '', path);
    setCurrentPath(path);
    setGuardPinInput('');
    setGuardError(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('construction_monitor_role');
    setAuthRole(null);
    navigate('/');
  };

  const handleLoginSuccess = (role: PortalRole, path: string) => {
    setAuthRole(role);
    navigate(path);
  };

  // Determine required role for current route
  const getRequiredRoleForPath = (path: string): PortalRole | null => {
    if (path.startsWith('/foreman')) return 'foreman';
    if (path.startsWith('/pto')) return 'pto';
    if (path.startsWith('/hr')) return 'hr';
    if (path.startsWith('/admin')) return 'admin';
    return null;
  };

  const requiredRole = getRequiredRoleForPath(currentPath);

  // Role metadata for route guard PIN prompt
  const roleMetadata: Record<PortalRole, { title: string; pin: string }> = {
    foreman: { title: 'Полевой PWA-портал Прораба', pin: '0000' },
    pto: { title: 'Инженерный портал ПТО & АОСР', pin: '1111' },
    hr: { title: 'Кадровый портал & Охрана Труда', pin: '2222' },
    admin: { title: 'Панель Главного Инженера & 2D-Шахматка', pin: '3333' }
  };

  const handleGuardPinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!requiredRole) return;
    const meta = roleMetadata[requiredRole];

    if (guardPinInput === meta.pin) {
      localStorage.setItem('construction_monitor_role', requiredRole);
      setAuthRole(requiredRole);
      setGuardPinInput('');
      setGuardError(null);
    } else {
      setGuardError(`Неверный PIN-код! Введите ${meta.pin} для роли ${meta.title}`);
      setGuardPinInput('');
    }
  };

  const handleGuardKeypadDigit = (digit: string) => {
    if (!requiredRole) return;
    const meta = roleMetadata[requiredRole];

    if (guardPinInput.length < 4) {
      const updated = guardPinInput + digit;
      setGuardPinInput(updated);
      setGuardError(null);
      if (updated.length === 4) {
        if (updated === meta.pin) {
          localStorage.setItem('construction_monitor_role', requiredRole);
          setAuthRole(requiredRole);
          setGuardPinInput('');
        } else {
          setGuardError(`Неверный PIN-код! Подсказка: ${meta.pin}`);
          setGuardPinInput('');
        }
      }
    }
  };

  // Render Root Launcher if on '/'
  if (currentPath === '/' || !requiredRole) {
    return (
      <LauncherModule
        onLoginSuccess={(role, path) => handleLoginSuccess(role, path)}
      />
    );
  }

  // Check if current user is authorized for the requested path
  const isAuthorized = authRole === requiredRole || authRole === 'admin';

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col font-sans selection:bg-amber-500 selection:text-slate-950">
      {/* 100% Autonomous Header for each portal route */}
      <PortalHeader
        role={requiredRole}
        onLogout={handleLogout}
        serverStatus={serverStatus}
        alertsCount={alertsCount}
      />

      {/* Main Content Workspace - 100% Full Width on Desktop */}
      <main className="flex-1 w-full px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {isAuthorized ? (
          <>
            {requiredRole === 'foreman' && <ForemanModule />}
            {requiredRole === 'pto' && <PtoModule />}
            {requiredRole === 'hr' && <HRModule />}
            {requiredRole === 'admin' && <AdminModule />}
          </>
        ) : (
          /* Route Guard PIN Modal Overlay */
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl space-y-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-200 text-amber-700 mx-auto flex items-center justify-center">
                <Lock className="w-7 h-7" />
              </div>

              <div>
                <h2 className="text-xl font-black text-slate-900">
                  Требуется авторизация
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  Введите ролевой PIN-код для доступа к «{roleMetadata[requiredRole].title}» (PIN: {roleMetadata[requiredRole].pin})
                </p>
              </div>

              <form onSubmit={handleGuardPinSubmit} className="space-y-4">
                <div className="flex justify-center items-center space-x-3">
                  {[0, 1, 2, 3].map((idx) => (
                    <div
                      key={idx}
                      className={`w-12 h-14 rounded-2xl border-2 flex items-center justify-center text-xl font-bold transition-all ${
                        guardPinInput.length > idx
                          ? 'border-amber-500 bg-amber-50 text-slate-900 shadow-sm'
                          : 'border-slate-200 bg-slate-50 text-slate-300'
                      }`}
                    >
                      {guardPinInput.length > idx ? '●' : ''}
                    </div>
                  ))}
                </div>

                {guardError && (
                  <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-xs font-semibold flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                    <span>{guardError}</span>
                  </div>
                )}

                {/* Onscreen Keypad */}
                <div className="grid grid-cols-3 gap-2.5 pt-2">
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
                    <button
                      key={digit}
                      type="button"
                      onClick={() => handleGuardKeypadDigit(digit)}
                      className="py-3 bg-slate-50 hover:bg-slate-100 active:bg-amber-100 border border-slate-200 rounded-2xl text-lg font-bold text-slate-800 transition active:scale-95 shadow-sm"
                    >
                      {digit}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setGuardPinInput('')}
                    className="py-3 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-2xl text-xs font-bold text-slate-600 transition"
                  >
                    Сброс
                  </button>
                  <button
                    type="button"
                    onClick={() => handleGuardKeypadDigit('0')}
                    className="py-3 bg-slate-50 hover:bg-slate-100 active:bg-amber-100 border border-slate-200 rounded-2xl text-lg font-bold text-slate-800 transition active:scale-95 shadow-sm"
                  >
                    0
                  </button>
                  <button
                    type="button"
                    onClick={() => setGuardPinInput(prev => prev.slice(0, -1))}
                    className="py-3 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-2xl text-xs font-bold text-slate-600 transition"
                  >
                    ⌫
                  </button>
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-2xl border border-slate-200 transition text-xs"
                  >
                    В ролевой холл
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-2xl shadow-md shadow-amber-500/20 transition text-xs"
                  >
                    Подтвердить
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>

      {/* Footer - Full Width */}
      <footer className="bg-white border-t border-slate-200 py-4 px-4 sm:px-6 lg:px-8 text-center text-xs text-slate-500 flex flex-col sm:flex-row items-center justify-between gap-2 w-full">
        <div>
          Монитор строительства • Электромонтажные работы (ЭОМ/ЭС/СС) &copy; 2026
        </div>
        <div className="text-[11px] text-slate-600 font-medium">
          Объект: <strong>ЖК «Северная Звезда»</strong> | Подрядчик: <strong>ООО «ЭлектроМонтажСтрой»</strong>
        </div>
      </footer>
    </div>
  );
};

export default App;
